/**
 * Remove wall and Restore wall (PRP-SKETCH-FIELD-3 J1-J4, H1, H3, H4; J6 makes
 * FIELD-2's "Open to" the same action). Each is one undo step (`History.run`).
 *
 * J1, the gap rule: a shared stretch removed end to end, floor to ceiling, leaves
 * no floor between the rooms. A room whose whole wall is the stretch ("fully
 * open") moves that wall out: both fully open, each by half the thickness (they
 * meet on the old centerline); one fully open, that room by the whole thickness
 * to the other room's face, and the other is unchanged. Neither (offset rooms):
 * the wall stays either side, so the opening is floor to ceiling in a wall that
 * keeps its thickness, with a one-line message.
 *
 * Everything Remove wall changes is recorded on the missing wall so Restore wall
 * returns the plan exactly (the Stop Condition):
 * - `missing.absorbed`: per room, how far its wall moved, the thickness it stored for
 *   the wall (DD-SF3.13: it comes back at that thickness) and any J2 note it wrote;
 * - `missing.removedOpenings`: the openings it took off the stretch (W2, DD-SF3.12);
 * - each crossing measurement's `adjustIn` / `baseFt` (J3, `recordAdjust`).
 * Only a wall that was 0 in before (a split line) comes back at the plan default,
 * split evenly between the rooms, with the check-dimensions note (H1, J4).
 */
import { IN_PER_FT, POLICY, inToFt } from './policy';
import { EPS, along as alongPt, clone, dist, edgeOf, findRoom, outN, round6, tidy } from './geom';
import { partnerOf, sharedMap } from './shared';
import { copyWall, setWallIn, wallIn } from './thickness';
import { addOpeningAt, overlapsOthers } from './openings';
import { missingFields } from './missingKinds';
import { noteCheckDims } from './walls';
import { clearStretch, lowerCeilingFt, sharedStretch, type Across } from './stretch';
import { crossing, heldBy, lineGroup, moveLine, normalAxis, recordAdjust } from './lineShift';
import { uid } from './plan';
import { cleanupZero } from './edit';
import { mergeAt } from './breaks';
import type { AbsorbedV2, Corner, Fail, Opening, Plan, RemovedOpeningV2, Room } from './types';

export type Absorbed = AbsorbedV2;

/** Inches compare within the core's coordinate precision (policy `coord_per_ft`), in inches. */
export const IN_TOL = EPS * IN_PER_FT;

const fmtIn = (n: number) => `${Number(n.toFixed(2))}"`;
const nowIso = () => new Date().toISOString();

export type RemoveWallResult =
  | {
    ok: true; oid: number; removed: number;
    /** Which J1 case applied. */
    open: 'both' | 'one' | 'neither';
    absorbed: Absorbed[];
    /** Rooms given the check-dimensions note (J2). */
    checkDims: string[];
    /** The J1 offset-case line for the screen. */
    message?: string;
  }
  | Fail<'not-shared'>;

/** The stretch covers the room's whole wall (within the export's fills-wall tolerance). */
function fullyOpen(r: Room, vid: number, lo: number, hi: number): boolean {
  const E = edgeOf(r, vid);
  if (!E) return false;
  const tol = inToFt(POLICY.export.fills_wall_tol_in);
  return lo <= tol && hi >= dist(E.a, E.b) - tol;
}

/** The openings on the shared stretch, both sides, as they are (W2): what `clearStretch` is about to take. */
function stretchOpenings(a: Across): RemovedOpeningV2[] {
  const out: RemovedOpeningV2[] = [];
  const take = (r: Room, vid: number, lo: number, hi: number) => r.openings.forEach((o, index) => {
    if (o.edgeStart !== vid || o.offFt >= hi - EPS || o.offFt + o.widthFt <= lo + EPS) return;
    out.push({ roomId: r.id, index, opening: clone(o) });
  });
  take(a.r, a.vid, a.iv.lo, a.iv.hi);
  if (a.back) take(a.q, a.iv.ovid, a.back.lo, a.back.hi);
  return out;
}

/**
 * Remove wall (J1-J3; J6 "Remove wall (open to <room>)"): the shared stretch of
 * wall `vid` of room `rid` facing `otherRid` (or the only room across). Openings
 * already on the stretch are replaced (and recorded, W2).
 */
export function removeWall(
  plan: Plan, rid: string, vid: number, otherRid?: string, opts: { now?: string } = {},
): RemoveWallResult {
  const M = sharedMap(plan);
  const a = sharedStretch(plan, M, rid, vid, otherRid);
  if (!a) return { ok: false, err: 'not-shared', message: 'Pick a wall between two rooms' };
  const ovid = a.iv.ovid;
  const fullR = fullyOpen(a.r, vid, a.iv.lo, a.iv.hi);
  const fullQ = !!a.back && fullyOpen(a.q, ovid, a.back.lo, a.back.hi);
  const ceil = lowerCeilingFt(a);
  const taken = stretchOpenings(a);
  const removed = clearStretch(plan, a);
  const withTaken = <T extends object>(m: T): T => (taken.length ? { ...m, removedOpenings: taken } : m);
  if (!fullR && !fullQ) return overlapRemove(plan, a, ceil, removed, withTaken);
  const ax = normalAxis(a.r, vid)!;
  const lineR = edgeOf(a.r, vid)!.a[ax], lineQ = edgeOf(a.q, ovid)!.a[ax];
  const mid = round6((lineR + lineQ) / 2);
  const movers: { r: Room; vid: number; to: number }[] = fullR && fullQ
    ? [{ r: a.r, vid, to: mid }, { r: a.q, vid: ovid, to: mid }]
    : fullR ? [{ r: a.r, vid, to: lineQ }] : [{ r: a.q, vid: ovid, to: lineR }];
  const now = opts.now ?? nowIso();
  const pair = new Set([a.r.id, a.q.id]);
  const absorbed: Absorbed[] = [];
  const checkDims: string[] = [];
  for (const mv of movers) {
    const ids = lineGroup(mv.r, mv.vid, ax);
    const held = heldBy(mv.r, ids, mv.vid, M, pair);
    const res = moveLine(mv.r, ids, ax, mv.to);
    const distanceIn = round6(Math.abs(res.d) * IN_PER_FT);
    // J3: the room's own measurements across the moved wall grow by the move, recorded.
    res.measured.forEach((x) => { if (Math.abs(x.after - x.before) > EPS) recordAdjust(x.m, Math.sign(x.after - x.before) * distanceIn); });
    const rec: Absorbed = { roomId: mv.r.id, distanceIn, wallIn: wallIn(mv.r, mv.vid) };
    setWallIn(mv.r, mv.vid, 0);
    // J2: the move still happens; a wall on the same line held by another room gets the note.
    if (held.length && distanceIn > IN_TOL) {
      const names = held.map((id) => findRoom(plan, id)?.name ?? id).join(', ');
      const other = mv.r === a.r ? a.q.name : a.r.name;
      const reason = `Wall to ${names} moved ${fmtIn(distanceIn)} when the wall to ${other} was removed`;
      if (mv.r.checkDims) rec.prevNote = { ...mv.r.checkDims };
      rec.note = reason;
      noteCheckDims(mv.r, reason, now);
      checkDims.push(mv.r.id);
    }
    absorbed.push(rec);
  }
  tidy(plan);
  // The rooms now meet: the stretch, found again, carries the linked missing wall.
  const b = sharedStretch(plan, sharedMap(plan), rid, vid, a.q.id) ?? a;
  const missing = withTaken({ ...missingFields(POLICY.open_to_kind, ceil), absorbed });
  const res = addOpeningAt(plan, a.r, vid, b.iv.lo, { kind: 'missing', widthFt: b.iv.hi - b.iv.lo, missing });
  return { ok: true, oid: res.oid, removed, open: fullR && fullQ ? 'both' : 'one', absorbed: absorbed.map((x) => ({ ...x })), checkDims };
}

/**
 * FIELD-4 K1 (supersedes FIELD-3 J1's offset case): two rooms that only partly meet.
 * The overlapping span becomes its own 0 in wall segment, missing end to end; the rest
 * of each room's wall stays a normal wall. In the span each room meets the old
 * centerline, so each grows half the thickness there: the span is bumped out between
 * two short jogs (`bump`, recorded so Restore wall takes them out again).
 */
function overlapRemove(
  plan: Plan, a: Across, ceil: number, removed: number,
  withTaken: <T extends object>(m: T) => T,
): RemoveWallResult {
  const ax = normalAxis(a.r, a.vid)!;
  const lineR = edgeOf(a.r, a.vid)!.a[ax], lineQ = edgeOf(a.q, a.iv.ovid)!.a[ax];
  const mid = round6((lineR + lineQ) / 2);
  const absorbed: Absorbed[] = [];
  let rVid = a.vid;
  for (const side of [{ r: a.r, vid: a.vid }, { r: a.q, vid: a.iv.ovid }]) {
    if (side.r === a.q && !a.back) continue;
    // The stretch on this room's own wall (each side's interval is in its own wall's frame).
    const iv = side.r === a.r ? a.iv : a.back!;
    const [lo, hi] = [iv.lo, iv.hi];
    const wallBefore = wallIn(side.r, side.vid);
    const b = bumpOut(plan, side.r, side.vid, lo, hi, ax, mid);
    if (side.r === a.r) rVid = b.vid;
    const rec: Absorbed = { roomId: side.r.id, distanceIn: b.distanceIn, wallIn: wallBefore };
    if (b.bump.length) rec.bump = b.bump;
    absorbed.push(rec);
  }
  tidy(plan);
  const bs = sharedStretch(plan, sharedMap(plan), a.r.id, rVid, a.q.id);
  const missing = withTaken({ ...missingFields(POLICY.open_to_kind, ceil), absorbed });
  const lo = bs ? bs.iv.lo : 0;
  const E = edgeOf(a.r, rVid)!;
  const res = addOpeningAt(plan, a.r, rVid, lo, { kind: 'missing', widthFt: bs ? bs.iv.hi - bs.iv.lo : dist(E.a, E.b), missing });
  return { ok: true, oid: res.oid, removed, open: 'neither', absorbed: absorbed.map((x) => ({ ...x })), checkDims: [] };
}

/**
 * K1: move the stretch [lo, hi] of wall `vid` (offsets from its start) to coordinate `to`
 * on axis `ax`, with a zero-length-then-moved pair of corners at each end inside the
 * wall (the jog). The stretch becomes 0 in; measurements crossing it change by the move,
 * recorded (J3). Returns the stretch's wall (start corner) and the corners added.
 */
function bumpOut(
  plan: Plan, r: Room, vid: number, lo: number, hi: number, ax: 'x' | 'y', to: number,
): { vid: number; bump: number[]; distanceIn: number } {
  const E = edgeOf(r, vid)!;
  const L = dist(E.a, E.b);
  const tol = inToFt(POLICY.export.fills_wall_tol_in);
  const mk = (s: number): Corner => ({ id: uid(plan), ...alongPt(E.a, E.b, s) });
  const ins: Corner[] = [];
  let start = E.a, end = E.b, after: Corner | null = null;
  if (lo > tol) { const k1 = mk(lo), k2 = mk(lo); ins.push(k1, k2); start = k2; }
  if (hi < L - tol) { const k3 = mk(hi), k4 = mk(hi); ins.push(k3, k4); end = k3; after = k4; }
  r.corners.splice(r.corners.indexOf(E.a) + 1, 0, ...ins);
  ins.forEach((c) => copyWall(r, E.a.id, c.id));
  // Openings beyond the stretch keep their world positions on the wall after the jog.
  if (after) r.openings.forEach((o) => { if (o.edgeStart === E.a.id && o.offFt >= hi - EPS) { o.edgeStart = after!.id; o.offFt = round6(o.offFt - hi); } });
  const ids = new Set<number>();
  for (let i = r.corners.indexOf(start); ; i = (i + 1) % r.corners.length) {
    ids.add(r.corners[i].id);
    if (r.corners[i] === end) break;
  }
  const mv = moveLine(r, ids, ax, to);
  const distanceIn = round6(Math.abs(mv.d) * IN_PER_FT);
  mv.measured.forEach((x) => { if (Math.abs(x.after - x.before) > EPS) recordAdjust(x.m, Math.sign(x.after - x.before) * distanceIn); });
  setWallIn(r, start.id, 0);
  return { vid: start.id, bump: ins.map((c) => c.id), distanceIn };
}

/**
 * Restore wall after K1: take the jog corners out again (zero-length first, then the
 * colinear ones). Returns the wall `vid` became (the original wall's start corner).
 */
function unbump(r: Room, bump: number[], vid: number): number {
  const merged = cleanupZero(r);
  for (const id of bump) mergeAt(r, merged.get(id) ?? id, merged);
  return merged.get(vid) ?? vid;
}

export type RestoreWallResult =
  | {
    ok: true;
    /** The wall thickness that came back, inches. */
    thicknessIn: number;
    /** Rooms whose wall moved back. */
    moved: string[];
    /** Rooms given the check-dimensions note (a split line, or a size measured since the removal). */
    checkDims: string[];
    /** Openings the removal took that could not come back (a linked pair counts once). */
    skipped: number;
    /** The user line for `skipped` ("1 door could not come back: ..."), when any. */
    skippedMessage?: string;
  }
  | Fail<'missing' | 'not-missing' | 'no-record' | 'failed'>;

/**
 * Give one room back what it absorbed (J4): its wall line moves back `giveIn`, its
 * recorded measurements shrink by exactly what they grew, its wall takes `wallTo`,
 * and the J2 note the removal wrote is cleared. Returns whether a size measured
 * after the removal changed (no record): the caller notes it.
 */
export function giveBack(room: Room, vid: number, giveIn: number, wallTo: number, x?: Absorbed): { moved: boolean; unrecorded: boolean } {
  const ax = normalAxis(room, vid), E = edgeOf(room, vid);
  if (!ax || !E) return { moved: false, unrecorded: false };
  const n = outN(room, E.a, E.b);
  const ids = lineGroup(room, vid, ax);
  const unrecorded = crossing(room, ids, ax).some((m) => m.adjustIn == null);
  const mv = moveLine(room, ids, ax, round6(E.a[ax] - n[ax] * giveIn / IN_PER_FT));
  mv.measured.forEach((y) => {
    if (y.m.adjustIn != null && Math.abs(y.after - y.before) > EPS) recordAdjust(y.m, Math.sign(y.after - y.before) * giveIn);
  });
  setWallIn(room, vid, wallTo);
  if (x?.note && room.checkDims?.reason === x.note) {
    if (x.prevNote) room.checkDims = { ...x.prevNote }; else delete room.checkDims;
  }
  const moved = Math.abs(mv.d) > EPS;
  return { moved, unrecorded: unrecorded && moved };
}

/** An opening `putBack` could not put back, and why. */
export interface SkippedOpening { roomId: string; opening: Opening; why: 'overlap' | 'fit' }

/**
 * Put the openings Remove wall took back where they were (W2): same ids, links,
 * positions and list order. An opening goes back on the wall the missing wall is on
 * now (`edgeOf`), and never over an opening that is there since, past the wall's end,
 * or under an id the room already uses. Returns the ones it left off.
 */
export function putBack(
  plan: Plan, removed: RemovedOpeningV2[], wallOf: (rid: string) => number | undefined, only?: string,
): SkippedOpening[] {
  const skipped: SkippedOpening[] = [];
  for (const x of [...removed].sort((p, q) => p.index - q.index)) {
    const room = findRoom(plan, x.roomId);
    if (!room || (only != null && room.id !== only)) continue;
    const o: Opening = clone(x.opening);
    o.edgeStart = wallOf(room.id) ?? o.edgeStart;
    const E = edgeOf(room, o.edgeStart);
    if (!E || o.offFt < -EPS || o.offFt + o.widthFt > dist(E.a, E.b) + EPS || room.openings.some((q) => q.id === o.id)) {
      skipped.push({ roomId: room.id, opening: o, why: 'fit' });
      continue;
    }
    if (overlapsOthers(room, o.edgeStart, o.offFt, o.offFt + o.widthFt)) {
      skipped.push({ roomId: room.id, opening: o, why: 'overlap' });
      continue;
    }
    room.openings.splice(Math.min(x.index, room.openings.length), 0, o);
  }
  return skipped;
}

const KIND_WORD: Record<Opening['kind'], string> = { door: 'door', window: 'window', missing: 'opening' };

/**
 * The user line for openings that could not come back. A linked pair (both copies
 * recorded) is one opening. "1 door could not come back: another opening is in its place."
 */
export function skippedLine(skipped: SkippedOpening[]): { count: number; message?: string } {
  const one = new Map<string, SkippedOpening>();
  skipped.forEach((s) => { const k = s.opening.link != null ? `L${String(s.opening.link)}` : `${s.roomId}:${String(s.opening.id)}`; if (!one.has(k)) one.set(k, s); });
  const all = [...one.values()];
  if (!all.length) return { count: 0 };
  const parts: string[] = [];
  for (const why of ['overlap', 'fit'] as const) {
    const xs = all.filter((s) => s.why === why);
    if (!xs.length) continue;
    const n = xs.length;
    const kinds = new Set(xs.map((s) => KIND_WORD[s.opening.kind] ?? KIND_WORD.missing));
    const word = kinds.size === 1 ? [...kinds][0] : KIND_WORD.missing;
    const what = `${n} ${word}${n > 1 ? 's' : ''} could not come back`;
    parts.push(why === 'overlap'
      ? `${what}: ${n > 1 ? 'other openings are in their place' : 'another opening is in its place'}.`
      : `${what}: ${n > 1 ? 'they no longer fit' : 'it no longer fits'} the wall.`);
  }
  return { count: all.length, message: parts.join(' ') };
}

/**
 * Restore wall (H1, J4): the exact reverse of `removeWall`. Each room gives back
 * exactly what it absorbed, at the thickness it stored for the wall (DD-SF3.13);
 * its measurements lose what they gained; its J2 note goes; the openings the
 * removal took come back (W2); both copies of the missing wall go. A wall that was
 * 0 in (a split line: every room absorbed 0) comes back at the plan's interior
 * default, split evenly, and both rooms get the note. A FIELD-2 split line (0 in, no
 * record) reads as both rooms having absorbed 0. A missing wall in a wall that kept
 * its thickness (no record, a gap) just goes, giving back what it took.
 * `keepRemoved: false` leaves the taken openings off (a kind switch replaces them).
 */
export function restoreWall(
  plan: Plan, rid: string, oid: number, opts: { now?: string; keepRemoved?: boolean } = {},
): RestoreWallResult {
  // Never throws (re-review item 1): any internal failure, or a result that is not a
  // valid plan, puts the plan back as it was and says so.
  const before = clone(plan);
  const saneBefore = new Set(plan.rooms.filter(roomSane).map((x) => x.id));
  let res: RestoreWallResult;
  try {
    res = restoreWallInner(plan, rid, oid, opts);
  } catch {
    res = { ok: false, err: 'failed', message: RESTORE_FAILED };
  }
  // A room that was valid before must be valid after (one already broken is not this restore's).
  if (res.ok && plan.rooms.some((x) => saneBefore.has(x.id) && !roomSane(x))) res = { ok: false, err: 'failed', message: RESTORE_FAILED };
  if (!res.ok) { plan.rooms = before.rooms; plan.next = before.next; }
  return res;
}

const RESTORE_FAILED = 'This wall could not be restored: its saved record does not fit the plan.';
const NO_RECORD = 'This wall cannot be restored: its saved record was damaged. Change its kind or delete it instead.';

/** Every number Restore wall writes is finite, every opening sits on its wall, opening ids are unique. */
function roomSane(r: Room): boolean {
  const tMax = POLICY.export.wall_thickness_max_in;
  const far = POLICY.export.max_plan_ft;
  if (!r.corners.every((c) => Math.abs(c.x) <= far && Math.abs(c.y) <= far)) return false; // false for NaN too
  if (!r.measurements.every((m) => Number.isFinite(m.valueFt))) return false;
  if (!Object.values(r.walls ?? {}).every((w) => Number.isFinite(w.thicknessIn) && w.thicknessIn >= 0 && w.thicknessIn <= tMax)) return false;
  if (new Set(r.openings.map((o) => o.id)).size !== r.openings.length) return false;
  return r.openings.every((o) => {
    const E = edgeOf(r, o.edgeStart);
    return !!E && Number.isFinite(o.offFt) && Number.isFinite(o.widthFt) && o.widthFt > 0
      && o.offFt >= -EPS && o.offFt + o.widthFt <= dist(E.a, E.b) + EPS;
  });
}

function restoreWallInner(
  plan: Plan, rid: string, oid: number, opts: { now?: string; keepRemoved?: boolean },
): RestoreWallResult {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o) return { ok: false, err: 'missing', message: 'That opening is gone' };
  if (o.kind !== 'missing') return { ok: false, err: 'not-missing', message: 'Only a missing wall can be restored' };
  const P = partnerOf(plan, r, o);
  if (o.missing?.recordDropped || P?.o.missing?.recordDropped) return { ok: false, err: 'no-record', message: NO_RECORD };
  const sides = [{ r, vid: o.edgeStart }, ...(P ? [{ r: P.r, vid: P.o.edgeStart }] : [])];
  const tDefault = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in;
  let absorbed = o.missing?.absorbed ?? P?.o.missing?.absorbed;
  const taken = o.missing?.removedOpenings ?? P?.o.missing?.removedOpenings ?? [];
  if (!absorbed && P && gapFt(r, o.edgeStart, P.r, P.o.edgeStart) < EPS) {
    absorbed = sides.map((s) => ({ roomId: s.r.id, distanceIn: 0 }));
  }
  const takers = (absorbed ?? []).map((x) => ({ x, side: sides.find((s) => s.r.id === x.roomId) })).filter((t) => t.side);
  const total = round6(takers.reduce((s, t) => s + t.x.distanceIn, 0));
  const splitLine = takers.length > 0 && total <= IN_TOL;
  const thicknessIn = splitLine ? tDefault : takers.length ? total : wallIn(r, o.edgeStart);
  const res: RestoreWallResult & { ok: true } = { ok: true, thicknessIn, moved: [], checkDims: [], skipped: 0 };
  r.openings = r.openings.filter((q) => q !== o);
  if (P) P.r.openings = P.r.openings.filter((q) => q !== P.o);
  const now = opts.now ?? nowIso();
  for (const { x, side } of takers) {
    const room = side!.r;
    const giveIn = splitLine ? tDefault / takers.length : x.distanceIn;
    const back = giveBack(room, side!.vid, giveIn, splitLine ? tDefault : x.wallIn ?? total, x);
    if (x.bump?.length) side!.vid = unbump(room, x.bump, side!.vid); // FIELD-4 K1: the jogs go
    if (back.moved) res.moved.push(room.id);
    // A split line (H1), or a size measured after the removal that changed with no record: the note.
    if (splitLine || back.unrecorded) {
      const other = sides.find((s) => s.r !== room)?.r.name;
      const to = other ? ` to ${other}` : '';
      noteCheckDims(room, `Wall${to} restored at ${fmtIn(res.thicknessIn)}; ${fmtIn(x.distanceIn)} had been opened into this room`, now);
      res.checkDims.push(room.id);
    }
  }
  if (opts.keepRemoved !== false) {
    const left = skippedLine(putBack(plan, taken, (id) => sides.find((s) => s.r.id === id)?.vid));
    res.skipped = left.count;
    if (left.message) res.skippedMessage = left.message;
  }
  tidy(plan);
  return res;
}

/** The gap between two facing walls, feet. */
function gapFt(r: Room, vid: number, q: Room, ovid: number): number {
  const ax = normalAxis(r, vid), E = edgeOf(r, vid), F = edgeOf(q, ovid);
  return ax && E && F ? Math.abs(E.a[ax] - F.a[ax]) : Infinity;
}

/** J1's record of an absorbed wall (written by `removeWall` and `doSplit`), on either copy. */
export function absorbedOf(plan: Plan, r: Room, o: Opening): boolean {
  return !!(o.missing?.absorbed?.length || partnerOf(plan, r, o)?.o.missing?.absorbed?.length);
}

/**
 * J4 before J5 / E13: restore the wall a removed-wall missing opening absorbed and put
 * the opening back on it (same id, position and width, no J1 record) so a kind switch
 * or a partial kind (W1) runs on a real wall. The room across gets the linked copy (its
 * id kept too). The openings the removal took stay off: this opening takes the wall.
 */
export function restoreUnder(plan: Plan, r: Room, o: Opening): (RestoreWallResult & { ok: true }) | Fail<'absorbed'> {
  const P = partnerOf(plan, r, o);
  const keep = { id: o.id, vid: o.edgeStart, offFt: o.offFt, widthFt: o.widthFt, pid: P?.o.id };
  const spec = clone(o);
  delete spec.link;
  if (spec.missing) { delete spec.missing.absorbed; delete spec.missing.removedOpenings; }
  const back = restoreWall(plan, r.id, o.id, { keepRemoved: false });
  if (!back.ok || !edgeOf(r, keep.vid)) return { ok: false, err: 'absorbed', message: 'Restore the wall first.' };
  const res = addOpeningAt(plan, r, keep.vid, keep.offFt, { ...spec, widthFt: keep.widthFt });
  r.openings.find((q) => q.id === res.oid)!.id = keep.id;
  if (res.partner && keep.pid != null) {
    const q = findRoom(plan, res.partner.rid)?.openings.find((x) => x.id === res.partner!.oid);
    if (q) q.id = keep.pid;
  }
  return back;
}
